import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Camera, UserPlus, Loader2, Share2, Copy, MessageCircle, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { autenticacaoService } from "@/services/autenticacaoService";
import { useDispositivosStore } from "@/stores/dispositivosStore";
import { AppConfig } from "@/datasources/AppConfig";
import { encryptToken, decryptId } from "@/utils/tokenCrypto";

interface AgenteCreateFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const isGuid = (v: unknown): v is string => typeof v === "string" && GUID_RE.test(v.trim());

function extrairUsuarioId(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;

  let fallbackId: string | undefined;
  const visited = new Set<object>();
  const stack: unknown[] = [payload];

  while (stack.length > 0) {
    const current = stack.pop();
    if (!current || typeof current !== "object" || visited.has(current as object)) continue;
    visited.add(current as object);
    const obj = current as Record<string, unknown>;

    const usuario = obj.usuario ?? obj.user;
    if (usuario && typeof usuario === "object") {
      const uid = (usuario as Record<string, unknown>).id;
      if (isGuid(uid)) return uid;
    }

    if (isGuid(obj.usuarioId)) return obj.usuarioId as string;
    if (isGuid(obj.userId)) return obj.userId as string;

    if (fallbackId === undefined && isGuid(obj.id)) {
      fallbackId = obj.id as string;
    }

    for (const value of Object.values(obj)) {
      if (value && typeof value === "object") stack.push(value);
    }
  }

  return fallbackId;
}

export function AgenteCreateForm({ onSuccess, onCancel }: AgenteCreateFormProps) {
  const { toast } = useToast();
  const { adicionarDispositivo } = useDispositivosStore();
  const [isLoading, setIsLoading] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    fotoPerfilFile: null as File | null,
    fotoPerfilPreview: "",
  });

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm((prev) => ({ ...prev, fotoPerfilFile: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm((prev) => ({ ...prev, fotoPerfilPreview: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const updateField = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.nome || !form.email) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      let ouvidoriaId = AppConfig.ouvidoriaId;
      if (!ouvidoriaId) {
        const ouvidoriaIdCifrado = localStorage.getItem('ouvidoriaId');
        if (ouvidoriaIdCifrado) {
          ouvidoriaId = await decryptId(ouvidoriaIdCifrado);
        }
      }

      if (!ouvidoriaId) {
        throw new Error('ID da ouvidoria não encontrado.');
      }

      // 1) Cria o USUÁRIO na API de autenticação
      const usuarioResult = await autenticacaoService.criarUsuario({
        id: form.email,
        nome: form.nome,
      });

      if ('failure' in usuarioResult) {
        toast({
          title: "Erro ao criar usuário",
          description: usuarioResult.failure.message || "Não foi possível criar o usuário. Tente novamente.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      const usuarioIdCriado = usuarioResult.success.usuario?.id;
      const tokenAcesso = usuarioResult.success.tokenAcesso;

      // 2) Cria o AGENTE vinculado à ouvidoria.
      // A requisição é autenticada com o tokenAcesso retornado na criação do usuário
      // (e não com o token do usuário logado).
      const formData = new FormData();
      formData.append("nome", form.nome);
      if (form.email) formData.append("email", form.email);
      if (form.telefone) formData.append("telefone", form.telefone);
      if (form.fotoPerfilFile) formData.append("arquivos[0]", form.fotoPerfilFile);

      const result = await dataSource.post({
        endpoint: `v1/ouvidorias/${ouvidoriaId}/agentes`,
        requiredToken: false,
        headers: {
          "Accept": "application/force+json",
          "Authorization": `bearer ${tokenAcesso}`,
        },
        body: formData as unknown as Record<string, unknown>,
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao criar agente",
          description: result.failure.messages.join(", ") || "Ocorreu um erro ao criar o agente. Tente novamente.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      const responseBody = result.success.body as { id?: string; agenteId?: string } | undefined;
      const agenteId = isGuid(responseBody?.id)
        ? responseBody?.id
        : (isGuid(responseBody?.agenteId));

      const usuarioId = isGuid(usuarioIdCriado)
        ? usuarioIdCriado
        : extrairUsuarioId(result.success.body);

      // 3) Registra o dispositivo atual (administrador) no backend.
      // O dispositivoId é gerado via UUID v5 a partir da fingerprint do navegador/SO.
      try {
        await adicionarDispositivo({});
      } catch (deviceError) {
        // Não bloqueia o fluxo de criação do agente; apenas loga o erro.
        console.error("Erro ao registrar dispositivo ao criar agente:", deviceError);
      }

      // Encriptar dados (usuarioId, email, tokenAcesso) para incluir na URL
      let activationLink = null;
      if (usuarioId && tokenAcesso) {
        const data = {
          usuarioId,
          email: form.email,
          tokenAcesso
        };
        const encrypted = await encryptToken(JSON.stringify(data));
        activationLink = `${window.location.origin}/autenticacao-dois-fatores?d=${encodeURIComponent(encrypted)}`;
      }

      // Modo admin: não faz login como o novo agente, apenas mostra o link
      if (activationLink) {
        setShareLink(activationLink);
      } else {
        toast({
          title: "Agente criado com sucesso!",
          description: "O agente foi cadastrado e poderá fazer login.",
        });
        onSuccess();
      }
    } catch (error) {
      toast({
        title: "Erro ao criar agente",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      toast({
        title: "Link copiado!",
        description: "Compartilhe o link com o novo agente para ativar a conta.",
      });
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (err) {
      toast({
        title: "Não foi possível copiar",
        description: "Copie o link manualmente.",
        variant: "destructive",
      });
    }
  };

  const handleShareWhatsApp = () => {
    if (!shareLink) return;
    const message = `Olá! Foi criada uma conta para você no sistema UrbNet. Acesse o link para ativar sua conta: ${shareLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShareFacebook = () => {
    if (!shareLink) return;
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`;
    window.open(facebookUrl, '_blank');
  };

  const handleShareGmail = () => {
    if (!shareLink) return;
    const subject = encodeURIComponent('Ativação de conta UrbNet');
    const body = encodeURIComponent(`Olá!\n\nFoi criada uma conta para você no sistema UrbNet.\n\nAcesse o link abaixo para ativar sua conta:\n\n${shareLink}\n\nAtenciosamente.`);
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank');
  };

  const handleContinue = () => {
    setShareLink(null);
    onSuccess();
  };

  if (shareLink) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <UserPlus className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">Agente criado!</h3>
            <p className="text-sm text-muted-foreground">Compartilhe o link de ativação</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Envie este link para o novo agente. Ao acessá-lo, ele poderá ativar a conta,
          verificar o código enviado por e-mail e definir a própria senha.
        </p>

        {/* Botões de compartilhamento */}
        <div className="grid grid-cols-3 gap-2">
          <Button
            type="button"
            onClick={handleShareWhatsApp}
            className="h-12 rounded-xl bg-green-500 hover:bg-green-600 text-white"
          >
            <MessageCircle className="h-5 w-5" />
          </Button>
          <Button
            type="button"
            onClick={handleShareFacebook}
            className="h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Share2 className="h-5 w-5" />
          </Button>
          <Button
            type="button"
            onClick={handleShareGmail}
            className="h-12 rounded-xl bg-red-500 hover:bg-red-600 text-white"
          >
            <Mail className="h-5 w-5" />
          </Button>
        </div>

        {/* Input de link para copiar manualmente */}
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={shareLink}
            onFocus={(e) => e.currentTarget.select()}
            className="h-12 rounded-xl text-sm"
          />
          <Button
            type="button"
            onClick={handleCopyLink}
            className="h-12 px-4 rounded-xl shrink-0"
          >
            {linkCopied ? (
              <Copy className="h-5 w-5" />
            ) : (
              <Copy className="h-5 w-5" />
            )}
          </Button>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleContinue} className="rounded-xl">
            Fechar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Avatar Upload */}
      <div className="flex flex-col items-center gap-3 mb-2">
        <div className="relative group">
          <Avatar className="h-20 w-20 border-4 border-border/50 shadow-lg">
            {form.fotoPerfilPreview ? (
              <AvatarImage src={form.fotoPerfilPreview} alt="Foto de perfil" />
            ) : (
              <AvatarFallback className="bg-gradient-to-br from-primary/20 to-secondary/20 text-3xl">
                {form.nome ? form.nome.charAt(0).toUpperCase() : "👤"}
              </AvatarFallback>
            )}
          </Avatar>
          <label
            htmlFor="avatar-upload"
            className="absolute bottom-0 right-0 p-2 rounded-full bg-primary text-primary-foreground cursor-pointer shadow-lg hover:bg-primary/90 transition-all duration-300 group-hover:scale-110"
          >
            <Camera className="h-4 w-4" />
          </label>
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            className="hidden"
          />
        </div>
        <p className="text-xs text-muted-foreground">Clique para adicionar foto</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="nome">Nome completo <span className="text-destructive">*</span></Label>
        <Input
          id="nome"
          placeholder="Nome do agente"
          value={form.nome}
          onChange={(e) => updateField("nome", e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">E-mail <span className="text-destructive">*</span></Label>
        <Input
          id="email"
          type="email"
          placeholder="email@exemplo.com"
          value={form.email}
          onChange={(e) => updateField("email", e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="telefone">Telefone</Label>
        <Input
          id="telefone"
          type="tel"
          placeholder="(11) 99999-9999"
          value={form.telefone}
          onChange={(e) => updateField("telefone", e.target.value)}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-xl">
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading} className="rounded-xl">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <UserPlus className="h-4 w-4 mr-2" />
              Criar Agente
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
